import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject } from 'rxjs';

import { LoggerService } from 'app/services/logger.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { Permission } from '../bridge-api/auth.service';
import { WithAuditContext } from '../http-mutating-operations.interceptor';
import { SemantriaAPIService } from '../semantria-api.service';
import { ConfigurationRoute } from './configuration-route.service';
import { SemantriaUser } from './semantria-users.service';

export interface Group {
  group_id: string;
  account_id: string;
  external_id?: string;
  group_name: string;
  creation_date?: Date;
  modified_date?: Date;
  members: string[];
  owner?: SemantriaUser;
  permissions: Permission[];
  routes?: ConfigurationRoute[];
}

export interface AvailableGroup {
  id: string;
  name: string;
}

const GROUP_ABBREV_NUM = 8;

@EnableClassTelemetry()
@Injectable()
export class GroupService extends SemantriaAPIService {
  private selectedGroupSubject = new BehaviorSubject<Group>(null);
  public selectedGroup$ = this.selectedGroupSubject.asObservable();
  private selectedGroup: Group;
  private sessionExpired = false;
  private visibleGroups: Group[] = [];

  constructor(
    private router: Router,
    httpClient: HttpClient,
    systemService: SystemService,
    private logger: LoggerService
  ) {
    super(httpClient, systemService);
  }

  getGroups(): Promise<Group[]> {
    return this.get('/groups/');
  }

  getGroup(id): Promise<Group> {
    return this.get(`/groups/${id}`);
  }

  @WithAuditContext('group-created', (_, args) => {
    return {
      group_name: args[0].group_name,
      group_id: args[0].group_id,
      account_id: args[0].account_id,
    };
  })
  createGroup(group: Group): Promise<Group> {
    group.members = [];
    return this.post<Group>('/groups/', { ...group });
  }

  @WithAuditContext('group-updated', (_, args) => {
    return {
      group_name: args[0].group_name,
      group_id: args[0].group_id,
      account_id: args[0].account_id,
    };
  })
  updateGroup(group: Group): Promise<Group> {
    return this.put<Group>(`/groups/${group.group_id}`, { ...group, members: [] });
  }

  @WithAuditContext('group-deleted', (_, args) => {
    return { id: args[0] };
  })
  deleteGroup(id: string): Promise<any> {
    return this.delete(`/groups/${id}`);
  }

  getMembersInGroup(groupId): Promise<SemantriaUser[]> {
    return this.get(`/groups/${groupId}/members/`);
  }

  @WithAuditContext('group-member-added', (_, args) => {
    return { groupId: args[0], userId: args[1] };
  })
  addGroupMember(groupId, userId): Promise<any> {
    return this.patch(`/groups/${groupId}/members/`, [userId]);
  }

  @WithAuditContext('group-members-added', (_, args) => {
    return { groupId: args[0], userIds: args[1] };
  })
  addGroupMembers(groupId: string, userIds: string[]): Promise<any> {
    return this.patch(`/groups/${groupId}/members/`, userIds);
  }

  addMemberToGroups(userId: string, groupIds: string[]): Promise<any> {
    return Promise.all(groupIds.map((id) => this.addGroupMember(id, userId)));
  }

  @WithAuditContext('group-member-deleted', (_, args) => {
    return { groupId: args[0], userId: args[1] };
  })
  removeGroupMember(groupId, userId): Promise<any> {
    return this.patch(`/groups/${groupId}/members/?action=delete`, [userId]);
  }

  @WithAuditContext('group-permission-deleted', (_, args) => {
    return { groupId: args[0], permissionId: args[1] };
  })
  async removeGroupPermission(groupId, permissionId): Promise<any> {
    const group = this.visibleGroups.find((g) => g.group_id === groupId);
    group.permissions = group.permissions.filter((p) => p.permission_id !== permissionId);

    return this.delete(`/groups/${groupId}/permissions/${permissionId}`);
  }

  @WithAuditContext('group-permission-added', (_, args) => {
    return { groupId: args[0], permission: args[1].permission_type };
  })
  async addGroupPermission(groupId, permission: Permission): Promise<Permission> {
    return this.post<Permission>(`/groups/${groupId}/permissions/`, { ...permission }).then(
      (res) => {
        this.visibleGroups.find((g) => g.group_id === groupId).permissions.push(res);
        return res;
      }
    );
  }

  public async refreshAndSelectActiveGroup(
    groupId: string = undefined,
    forceReload: boolean = false
  ) {
    const requestedGroup = groupId || this.localStorage.getItem('group');
    this.logger.debug('refreshAndSelectActiveGroup', {
      requestedGroup,
      forceReload,
      visibleGroups: this.visibleGroups,
    });

    if (forceReload || !this.visibleGroups || this.visibleGroups.length === 0) {
      this.visibleGroups = await this.getGroups();
      this.visibleGroups.sort((a, b) =>
        a.group_name.toLowerCase().localeCompare(b.group_name.toLowerCase())
      );
    }
    if (this.visibleGroups.length > 0) {
      let sg;
      if (requestedGroup) {
        const matches = this.visibleGroups.filter((group) =>
          group.group_id.startsWith(requestedGroup)
        );

        if (matches.length === 0) {
          this.logger.warn(`Requested group ${requestedGroup} not found`, {
            visibleGroups: this.visibleGroups,
          });
          this.selectedGroup = undefined;
          this.sessionExpired = false;
          if (groupId) {
            // if no matches and requestedGroup is coming from the provided groupId,
            // the user is trying to access a URL - we should prevent unauthorized access
            this.router.navigateByUrl('not-found', { skipLocationChange: true });
            return false;
          } else {
            // if no matches and requestedGroup is coming from localstorage,
            // the user is just about to log in with a different account - should select 'default' group
            sg = this.visibleGroups.find((group) => group.group_name === 'default');
          }
        } else {
          if (matches.length > 1) {
            // TODO: something more than warn
            this.logger.warn('Multiple group matches for ' + requestedGroup, {
              visibleGroups: this.visibleGroups,
            });
          }
          sg = matches[0];
        }
      } else {
        sg = this.visibleGroups.find((group) => group.group_name === 'default');
      }

      if (!sg) {
        sg = this.visibleGroups[0];
      }
      if (sg) {
        this.setSelectedGroup(sg, forceReload);
      }
      return true;
    }
    return false;
  }

  public setSelectedGroup(group: Group, force = false) {
    if (!force && group.group_id === this.selectedGroup?.group_id) {
      return;
    }

    this.localStorage.setItem('group', group.group_id);
    this.selectedGroup = group;
    this.sessionExpired = false;
    this.selectedGroupSubject.next(group);
  }

  public getSelectedGroupId(shorten = false): string {
    let groupId = this.selectedGroup ? this.selectedGroup.group_id : undefined;
    if (shorten) {
      groupId = GroupService.shortenGroupId(groupId);
    }
    return groupId;
  }

  static shortenGroupId(groupId: string) {
    if (groupId) {
      groupId = groupId.substring(0, GROUP_ABBREV_NUM);
    }
    return groupId;
  }

  public getSelectedGroup(): Group {
    return this.selectedGroup;
  }

  public getVisibleGroups(): Group[] {
    return this.visibleGroups;
  }

  public getDefaultHomepage() {
    return `groups/${this.getSelectedGroupId(true)}/projects`;
  }

  public isSessionExpired(): boolean {
    return this.sessionExpired;
  }

  public clear(reason?: string) {
    this.localStorage.removeItem('group');
    this.localStorage.removeItem('lastProjectVisited');
    this.visibleGroups = [];
    this.selectedGroup = undefined;
    this.sessionExpired = reason === 'SessionExpired';
  }
}
